import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getJson, postJson } from '../global/func';
import logo from '../assets/images/logo-mark.png';

// Creating an account, for someone who has just unboxed a Prayer Box.
//
// The code comes first, and it is the whole reason this screen exists: there is
// no route here that produces an account owning no device. Asking for it before
// the form also means a mistyped code costs one field rather than a filled-in
// page.
//
// The check is advisory. Between it and the submit, another person could claim
// the same box, so the server checks again and that answer is the one that
// counts -- and if the box is gone by then, the account is not created at all.

const CODE_ALPHABET = /[^23456789ABCDEFGHJKMNPQRSTUVWXYZ]/g;
const CODE_LENGTH = 8;
const MIN_PASSWORD = 10;

const CreateAccount = ({ onRegistered, onCancel, onSignIn, inviteToken = '' }) => {

    // Two doors into the same form.
    //
    // A claim code says someone is holding a new box, and the code is asked for
    // first because everything depends on it. An invitation says somebody else
    // already holds one and has asked them in -- there is no code to type, the
    // address is fixed by the invitation, and the only thing still missing is a
    // name and a password. So the invited route skips straight to the details
    // and never shows the code field at all.
    const invited = inviteToken !== '';

    const [step, setStep] = useState(invited ? 'checking' : 'code');
    // What the invitation turned out to be: who sent it and which box. Only the
    // server can say, and all of it belongs on the screen that greets them.
    const [invite, setInvite] = useState(null);
    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [signInHint, setSignInHint] = useState(false);

    // Resolve the invitation before asking for anything. A link that has expired
    // or been withdrawn should cost one sentence, not a filled-in form.
    useEffect(() => {
        if (!invited) return undefined;

        let cancelled = false;
        (async () => {
            const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/auth/invite/');
            const result = await getJson(url, { token: inviteToken });
            if (cancelled) return;

            if (result?.ok && result.data?.ok) {
                setInvite(result.data);
                // The address is the invitation's, not theirs to choose: the
                // link was sent to one person, and a form that could redirect it
                // would turn every forwarded email into a way in.
                setEmail(result.data.email || '');
                setStep(result.data.hasAccount ? 'sign-in' : 'details');
                return;
            }

            setStep('refused');
            setError(result?.data?.error
                || 'This invitation link could not be checked. Please ask for a new one.');
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteToken]);

    const codeChanged = (raw) => {
        // Codes never contain O, 0, I, 1 or L, so anything outside the alphabet
        // is dropped as it is typed rather than rejected after submitting.
        setCode(raw.toUpperCase().replace(CODE_ALPHABET, '').slice(0, CODE_LENGTH));
        if (error) setError('');
    };

    const checkCode = async (e) => {
        e.preventDefault();
        if (code.length !== CODE_LENGTH) {
            setError(`Enter all ${CODE_LENGTH} characters shown on your Prayer Box screen.`);
            return;
        }

        setBusy(true);
        setError('');
        const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/auth/check-code/');
        const result = await postJson(url, { claimCode: code });
        setBusy(false);

        if (result.ok) {
            setStep('details');
            return;
        }
        setError(result.status === 0
            ? 'Could not reach the server. Check your connection and try again.'
            : (result.data?.error || 'That code could not be checked. Please try again.'));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (name.trim() === '') { setError('Please give a name for the account.'); return; }
        if (password.length < MIN_PASSWORD) {
            setError(`Choose a password of at least ${MIN_PASSWORD} characters.`);
            return;
        }

        setBusy(true);
        setError('');
        setSignInHint(false);

        const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/auth/register/');
        // The invitation carries its own address, so the email is not sent with
        // it -- the server reads it from the token and would ignore this one
        // anyway. Sending it would only suggest it could be chosen.
        const result = await postJson(url, invited
            ? { inviteToken, name, phone, password }
            : { claimCode: code, name, email, phone, password });
        setBusy(false);

        if (result.ok && result.data?.auth_key) {
            // The server signs them in, so they arrive at their own box rather
            // than at a login form they have just set a password for.
            onRegistered(result.data);
            return;
        }

        setSignInHint(result.data?.signInHint === true);
        setError(result.status === 0
            ? 'Could not reach the server. Check your connection and try again.'
            : (result.data?.error || 'The account could not be created. Please try again.'));
    };

    return (
        <div className="column auth">
            <img className="auth__logo" src={logo} alt="Prayer Box" />
            <div>
                <h1 className="auth__title">
                    {step === 'code' ? 'Set up your Prayer Box'
                        : invited ? 'You have been invited'
                            : 'Create your account'}

                </h1>
                <div>

                    {step === 'checking' ? (
                        <div className="centered"><span className="spinner" /></div>
                    ) : step === 'refused' ? (
                        <>
                            <div className="notice notice--warn" role="alert">{error}</div>
                            <button type="button" className="btn btn--primary btn--block" onClick={onCancel}>
                                Go to sign in
                            </button>
                        </>
                    ) : step === 'sign-in' ? (
                        // They were invited, and signed up for an account of
                        // their own in the meantime. The invitation is still
                        // good -- it is just spent by signing in rather than by
                        // creating a second account they could not use.
                        <>
                            <div className="notice notice--info" role="status">
                                {invite?.invited_by} shared {invite?.device_name} with you.
                            </div>
                            <p className="card-text">
                                There is already an account for <strong>{invite?.email}</strong>.
                                Sign in with it and the box will be added to it straight away.
                            </p>
                            {/* Not onCancel: that throws the token away, and the
                                token is what the sign-in is for. This hands it
                                back to App still held, so the invitation is
                                spent the moment they are signed in. */}
                            <button type="button" className="btn btn--primary btn--block"
                                onClick={onSignIn || onCancel}>
                                Sign in
                            </button>
                        </>
                    ) : step === 'code' ? (
                        <form onSubmit={checkCode}>
                            <p className="card-text">
                                Your Prayer Box shows an eight-character code on the television once
                                it is connected to Wi-Fi. Enter it here to begin.
                            </p>

                            <div>
                                <label htmlFor="new-code">Code from your screen</label>
                                <input
                                    id="new-code"
                                    type="text"
                                    className="form-control form-control-lg text-center"
                                    style={{ letterSpacing: '0.4em', fontSize: '1.6rem' }}
                                    value={code}
                                    onChange={(e) => codeChanged(e.currentTarget.value)}
                                    placeholder="--------"
                                    autoComplete="off"
                                    autoCapitalize="characters"
                                    spellCheck="false"
                                    autoFocus
                                    disabled={busy} />
                                <small className="form-text text-muted">
                                    {code.length}/{CODE_LENGTH} characters
                                </small>
                            </div>

                            {error && <div className="notice notice--warn" role="alert">{error}</div>}

                            <button type="submit" className="btn btn--primary btn--block"
                                disabled={busy || code.length !== CODE_LENGTH}>
                                {busy
                                    ? <>Checking...</>
                                    : <>Continue</>}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={submit}>
                            <div className="notice notice--info" role="status">
                                <small>
                                    {invited
                                        ? `${invite?.invited_by} shared ${invite?.device_name} with you. `
                                          + 'Create your account below and it will be waiting for you.'
                                        : 'Code accepted. It will be linked to the account you create below.'}
                                </small>
                            </div>

                            <div>
                                <label htmlFor="new-name">Your name</label>
                                <input id="new-name" type="text" className="form-control"
                                    value={name} maxLength={50} autoFocus disabled={busy}
                                    onChange={(e) => { setName(e.currentTarget.value); if (error) setError(''); }} />
                            </div>

                            <div>
                                <label htmlFor="new-email">Email address</label>
                                {/* Fixed when invited. The link was sent to one
                                    address, and an editable field here would
                                    turn every forwarded email into a way in --
                                    the server reads the address off the token
                                    regardless, so an edit would only mislead. */}
                                <input id="new-email" type="email" className="form-control"
                                    value={email} maxLength={50} autoComplete="email"
                                    disabled={busy || invited} readOnly={invited}
                                    onChange={(e) => { setEmail(e.currentTarget.value); if (error) setError(''); }} />
                                <small className="form-text text-muted">
                                    {invited
                                        ? 'This is the address the invitation was sent to. Sign in with it from now on.'
                                        : 'Used to sign in, and to recover the account if you forget your password.'}
                                </small>
                            </div>

                            <div>
                                <label htmlFor="new-phone">Phone <span className="muted">(optional)</span></label>
                                <input id="new-phone" type="tel" className="form-control"
                                    value={phone} maxLength={20} disabled={busy}
                                    onChange={(e) => setPhone(e.currentTarget.value)} />
                            </div>

                            <div>
                                <label htmlFor="new-password">Password</label>
                                <input id="new-password" type="password" className="form-control"
                                    value={password} autoComplete="new-password" disabled={busy}
                                    onChange={(e) => { setPassword(e.currentTarget.value); if (error) setError(''); }} />
                                <small className="form-text text-muted">
                                    {password.length}/{MIN_PASSWORD} characters minimum
                                </small>
                            </div>

                            {error &&
                                <div className="notice notice--warn" role="alert">
                                    {error}
                                    {signInHint &&
                                        <div>
                                            <button type="button" className="btn btn-sm btn-outline-primary"
                                                onClick={invited ? (onSignIn || onCancel) : onCancel}>
                                                Sign in instead
                                            </button>
                                        </div>}
                                </div>}

                            <button type="submit" className="btn btn--primary btn--block"
                                disabled={busy || !email || !name || password.length < MIN_PASSWORD}>
                                {busy
                                    ? <>Creating your account...</>
                                    : invited
                                        ? <>Create account and join this box</>
                                        : <>Create account and link my box</>}
                            </button>

                            {/* Back goes to the code field, which the invited
                                route never showed and has nothing to put in. */}
                            {!invited &&
                                <button type="button"
                                    onClick={() => { setStep('code'); setError(''); }}>
                                    Back
                                </button>}
                        </form>
                    )}

                    {step === 'code' &&
                        <>
                            <button type="button" onClick={onCancel}>
                                I already have an account
                            </button>
                            <hr />
                            <p>
                                <small>
                                    No code on screen? The Prayer Box needs Wi-Fi first. If it is showing
                                    setup instructions, follow those, and the code will appear once it is
                                    online.
                                </small>
                            </p>
                        </>}
                </div>
            </div>
        </div>
    );
};

CreateAccount.propTypes = {
    onRegistered: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    // Invited, but the address already has an account: leave for the sign-in
    // form with the invitation still in hand. Absent on the claim-code route,
    // which has nothing to hand on.
    onSignIn: PropTypes.func,
    inviteToken: PropTypes.string,
};

export default CreateAccount;
